import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import type {
  OrderHistoryCartItemModel,
  OrderHistoryFilterSortPageModel,
  OrderHistoryItemModel,
  OrderHistoryModel,
  OrderHistoryRecordModel,
  ReceiptDocumentModel,
} from '@libs/services/models';
import { CartItemPayloadModel, CartModel, ShopCollectionResponseModel, ShopErrorItemModel } from '@libs/shop/models';
import { CartService, FileSaverService } from '@libs/shared/services';
import { StatusCodes } from 'http-status-codes';
import { firstValueFrom, Observable } from 'rxjs';
import { OrderHistoryRepositoryService } from './order-history-repository.service';

@Injectable({ providedIn: 'root' })
export class OrderHistoryService {
  public readonly isAddingToCart: WritableSignal<boolean> = signal(false);

  private readonly orderHistoryRepositoryService: OrderHistoryRepositoryService = inject(OrderHistoryRepositoryService);
  private readonly fileSaverService: FileSaverService = inject(FileSaverService);
  private readonly cartService: CartService = inject(CartService);

  public getOrderHistory(params: OrderHistoryFilterSortPageModel): Observable<OrderHistoryModel> {
    return this.orderHistoryRepositoryService.getOrderHistory(params);
  }

  public getOrderHistoryRecord(orderId: string): Observable<OrderHistoryRecordModel> {
    return this.orderHistoryRepositoryService.getOrderHistoryRecord(orderId);
  }

  public getOrderDocuments(orderId: string): Observable<ReceiptDocumentModel[]> {
    return this.orderHistoryRepositoryService.getOrderDocuments(orderId);
  }

  public downloadOrderDocument(document: ReceiptDocumentModel): Promise<void> {
    return firstValueFrom(this.orderHistoryRepositoryService.getOrderDocumentFile(document.fileName))
      .then(file => this.fileSaverService.saveBlob(file, document.fileName));
  }

  public addOrderToCart(order: OrderHistoryRecordModel): Promise<ShopCollectionResponseModel<CartModel>> {
    return this.addItemsToCart(order.items.map((item: OrderHistoryItemModel) => ({ item, quantity: item.amountOrdered })));
  }

  public addItemsToCart(cartItems: OrderHistoryCartItemModel[]): Promise<ShopCollectionResponseModel<CartModel>> {
    const { items, missingIdErrors } = this.getValidCartItems(cartItems);

    this.isAddingToCart.set(true);

    return firstValueFrom(this.cartService.addItemsCollection(items, missingIdErrors))
      .finally(() => void this.isAddingToCart.set(false));
  }

  // Order positions SAP marks as "not in assortment" arrive without an item number, and the glue
  // endpoint answers a null/empty id with a 500 that discards the whole collection. Such items are
  // reported as failed instead of being sent, so the valid rest still reaches the cart.
  private getValidCartItems(cartItems: OrderHistoryCartItemModel[]): {
    items: CartItemPayloadModel[];
    missingIdErrors: ShopErrorItemModel[];
  } {
    const items: CartItemPayloadModel[] = [];
    const missingIdErrors: ShopErrorItemModel[] = [];

    for (const { item, quantity } of cartItems) {
      const sku: string = item.sku.toString().trim();

      if (typeof item.sku === 'string' && sku.length > 0) {
        items.push({
          type: 'cart-item',
          id: sku,
          // Free / 0-price positions can arrive without an ordered amount; default to 1 so the cart never receives null.
          quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        });
      } else {
        missingIdErrors.push({
          status: StatusCodes.UNPROCESSABLE_ENTITY,
          message: `Position ohne Artikelnummer (${item.id} ${item.name})`,
        });
      }
    }

    return { items, missingIdErrors };
  }
}
